import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Phone, User, Lock, ArrowRight, X, MapPin, Store, FileText, Loader2, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import cite from '../images/cité.png'
const colorMap = {
  secondary: {
    base: '#f97316',
    hover: '#ea580c',
    light: '#fdba74',
    dark: '#c2410c',
    opacity10: 'rgba(249, 115, 22, 0.1)',
  },
  primary: {
    base: '#22c55e',
    hover: '#16a34a',
    light: '#86efac',
    opacity10: 'rgba(34, 197, 94, 0.1)',
  },
  accent: {
    base: '#3b82f6', // Bleu pour WhatsApp
    hover: '#2563eb',
    light: '#93c5fd', 
  },
  text: {
    primary: '#111827',
    secondary: '#6b7280',
    muted: '#9ca3af',
    dark: '#374151',
  },
  bg: {
    white: '#ffffff',
    light: '#f9fafb',
    card: '#ffffff',
    disabled: '#d1d5db',
  },
  border: {
    default: '#e5e7eb',
  },
  status: {
    error: '#ef4444',
    success: '#22c55e',
  },
  progress: {
    inactive: '#e5e7eb',
  }
};

const CATEGORIES = [
  'Repas préparés',
  'Snacks & Boissons',
  'Produits laitiers',
  'Pain & Pâtisseries',
  'Produits d\'épicerie',
  'Livres & Manuels',
  'Fournitures scolaires',
  'Papeterie & Matériel de dessin',
  'Accessoires informatique',
  'Vêtements',
  'Chaussures',
  'Accessoires',
  'Savons & Produits d\'hygiène',
  'Beauté & Cosmétiques',
  'Ustensiles de cuisine',
  'Décoration & Meubles',
  'Articles ménagers',
  'Sport & Fitness',
  'Jeux & Divertissement',
  'Transfert d\'unités',
  'Impression & Photocopie',
  'Autres services étudiants',
  'Autres'
];

const SITES = ['ABOBO 1', 'ABOBO 2'];
const BATIMENTS = [
  'A1','A2','A3','A4',
  'B1','B2','B3','B4',
  'C1','C2','C3','C4',
  'D1','D2','D3','D4',
  'E1','E2','E3','E4',
  'F1','F2','F3','F4',
  'G1','G2','G3','G4',
  'H1','H2','H3','H4'
];
const PALIERS = ['0','1', '2', '3', '4'];
const CHAMBRES = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

// Auth est géré par le contexte Supabase

export default function CrouVenteAuth() {
  const [isLogin, setIsLogin] = useState(false);
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    site: '',
    batiment: '',
    palier: '',
    chambre: '',
    categories: [],
    shopName: '',
    shopDescription: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [isInCite, setIsInCite] = useState(true); // true = en cité, false = pas en cité
  const [focusedField, setFocusedField] = useState(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Réinitialiser les champs d'adresse quand l'utilisateur change le statut cité
  useEffect(() => {
    if (!isInCite) {
      // Si pas en cité, on garde le site mais on reset les autres champs
      setFormData(prev => ({
        ...prev,
        batiment: '',
        palier: '',
        chambre: ''
      }));
    } else {
      // Si en cité, on reset tout pour forcer à choisir dans la liste
      setFormData(prev => ({
        ...prev,
        site: '',
        batiment: '',
        palier: '',
        chambre: ''
      }));
    }
  }, [isInCite]);

const sanitizePhone = (value) =>
  value.replace(/[\u202A-\u202E\u2066-\u2069\s]/g, '');

const handleInputChange = (e) => {
  const { name, value } = e.target;

  const cleanedValue =
    name === 'phone' ? sanitizePhone(value) : value;

  setFormData(prev => ({
    ...prev,
    [name]: cleanedValue
  }));

  if (error) setError('');
};


  const toggleCategory = (category) => {
    setFormData({
      ...formData,
      categories: formData.categories.includes(category)
        ? formData.categories.filter(c => c !== category)
        : [...formData.categories, category]
    });
  };

  const { login, register } = useAuth();

  const handleLogin = async () => {
    setIsLoading(true);
    setError('');

    try {
      const result = await login({
        phone: formData.phone,
        password: formData.password,
      });

      if (!result.success) {
        throw new Error(result.error || 'Erreur lors de la connexion');
      }

      console.log('Connexion réussie:', result.user);
      window.location.href = '/user';

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async () => {
    setIsLoading(true);
    setError('');

    try {
      const result = await register({
        name: formData.name,
        phone: formData.phone,
        password: formData.password,
        site: formData.site || null,
        batiment: formData.batiment || null,
        palier: formData.palier || null,
        chambre: formData.chambre || null,
        categories: formData.categories,
        shopName: formData.shopName,
        shopDescription: formData.shopDescription,
      });

      if (!result.success) {
        throw new Error(result.error || 'Erreur lors de l\'inscription');
      }

      console.log('Inscription réussie:', result.user);
      window.location.href = '/user';

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = () => {
    if (isLogin) {
      handleLogin();
    } else {
      if (step < 6) {
        setStep(step + 1);
      } else {
        handleRegister();
      }
    }
  };

  const navigate = useNavigate();

  const handleSkip = () => {
    console.log("Utilisateur a ignoré l'authentification");
    navigate('/');
  };

  const canProceed = () => {
    if (isLogin) {
      return formData.phone && formData.password;
    }
    
    if (step === 1) return formData.name.trim();
    if (step === 2) {
      // Si l'utilisateur est en cité, vérifier tous les champs
      if (isInCite) {
        return formData.site && formData.batiment && formData.palier && formData.chambre;
      }
      // Si pas en cité, vérifier seulement le site (localisation)
      return formData.site && formData.site.trim();
    }
    if (step === 3) return formData.categories.length > 0;
    if (step === 4) return formData.shopName.trim() && formData.shopDescription.trim();
    if (step === 5) return formData.phone.trim();
    if (step === 6) return formData.password && formData.confirmPassword && formData.password === formData.confirmPassword;
    return false;
  };

  const resetForm = () => {
    setFormData({
      name: '',
      site: '',
      batiment: '',
      palier: '',
      chambre: '',
      categories: [],
      shopName: '',
      shopDescription: '',
      phone: '',
      password: '',
      confirmPassword: ''
    });
    setStep(1);
    setIsInCite(true);
    setFocusedField(null);
    setError('');
  };

  const getRoomAddress = () => {
    if (formData.site && formData.batiment && formData.palier && formData.chambre) {
      return `${formData.site} - Bâtiment ${formData.batiment} - Palier ${formData.palier} - Chambre ${formData.chambre}`;
    }
    return '';
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: '#fafafa' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=DM+Sans:wght@400;500;600;700&display=swap');
        
        * {
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
        }
        
        h1, h2, h3, .display-text {
          font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideInFromLeft {
          from { 
            opacity: 0;
            transform: translateX(-40px);
          }
          to { 
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideInFromRight {
          from { 
            opacity: 0;
            transform: translateX(40px);
          }
          to { 
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(30px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-20px) rotate(3deg); }
          66% { transform: translateY(-10px) rotate(-3deg); }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.8; }
        }
        
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }

        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out;
        }
        
        .animate-slideInLeft {
          animation: slideInFromLeft 0.7s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        .animate-slideInRight {
          animation: slideInFromRight 0.7s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        .animate-slideUp {
          animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.5s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-pulse {
          animation: pulse 2s ease-in-out infinite;
        }

        .animate-gradient {
          background-size: 200% 200%;
          animation: gradientShift 8s ease infinite;
        }
        
        .stagger-1 { animation-delay: 0.1s; }
        .stagger-2 { animation-delay: 0.2s; }
        .stagger-3 { animation-delay: 0.3s; }
        .stagger-4 { animation-delay: 0.4s; }
        .stagger-5 { animation-delay: 0.5s; }
        .stagger-6 { animation-delay: 0.6s; }
        
        .glass-effect {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }
        
        .input-focus-ring:focus-within {
          box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.1);
        }
        
        .category-button {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .category-button:hover {
          transform: translateY(-2px);
        }
        
        .category-button.selected {
          transform: scale(1.02);
        }

        .shimmer-effect {
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.6) 50%,
            transparent 100%
          );
          background-size: 1000px 100%;
          animation: shimmer 2s infinite;
        }

        .floating-shapes {
          position: absolute;
          width: 100%;
          height: 100%;
          overflow: hidden;
          pointer-events: none;
          z-index: 0;
        }

        .shape {
          position: absolute;
          opacity: 0.1;
        }

        .shape-1 {
          top: 10%;
          left: 5%;
          width: 200px;
          height: 200px;
          background: ${colorMap.secondary.base};
          border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
          animation: float 8s ease-in-out infinite;
        }

        .shape-2 {
          top: 60%;
          right: 10%;
          width: 150px;
          height: 150px;
          background: ${colorMap.primary.base};
          border-radius: 70% 30% 30% 70% / 70% 70% 30% 30%;
          animation: float 10s ease-in-out infinite reverse;
        }

        .shape-3 {
          bottom: 20%;
          left: 15%;
          width: 100px;
          height: 100px;
          background: ${colorMap.secondary.base};
          border-radius: 50%;
          animation: float 7s ease-in-out infinite;
          animation-delay: 2s;
        }

        @media (max-width: 768px) {
          .shape {
            opacity: 0.05;
          }
        }
      `}</style>

      {/* Floating Shapes Background */}
      <div className="floating-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:flex min-h-screen relative z-10 flex-row">
        {/* Left Side - Image Hero Section */}
        <div className="w-1/2 relative overflow-hidden order-1">
          <div className="absolute inset-0 bg-black/40"></div>
          <img 
            src={cite}
            alt="Cité Universitaire" 
            className="absolute inset-0 w-full h-full object-cover"
          />
          
          {/* Overlay Content */}
          <div className="relative h-full flex flex-col justify-between p-12 text-white">
            {/* Logo */}
           <div className="flex items-center gap-3 rounded-full">
  <img
    src="/logo-image.png"
    alt="Crou Vente"
    className="h-14 w-auto rounded-full"
  />
</div>

            {/* Hero Text */}
            <div className={`space-y-6 ${mounted ? 'animate-slideUp stagger-2' : 'opacity-0'}`}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border" style={{ 
                backgroundColor: colorMap.secondary.base,
                borderColor: colorMap.secondary.light
              }}>
                <Sparkles size={16} className="text-white" />
                <span className="text-sm font-medium text-white">Plateforme étudiante #1</span>
              </div>
              
              <div className="backdrop-blur-sm bg-black/20 p-6 rounded-2xl inline-block">
                <h2 className="text-5xl font-bold leading-tight">
                  Vendez et achetez
                  <br />
                  <span className="text-6xl" style={{ color: colorMap.secondary.base }}>entre étudiants</span>
                </h2>
              </div>
              
              <p className="text-lg text-white max-w-md leading-relaxed backdrop-blur-sm bg-black/20 p-4 rounded-xl inline-block">
                Rejoignez la communauté de vente la plus active de votre campus. 
                Simple, rapide et sécurisé.
              </p>

              {/* Stats */}
              <div className="flex gap-8 pt-4">
                <div className={`${mounted ? 'animate-fadeIn stagger-3' : 'opacity-0'}`}>
                  <div className="text-3xl font-bold" style={{ color: colorMap.secondary.base }}>2500+</div>
                  <div className="text-sm text-white/90">Étudiants</div>
                </div>
                <div className={`${mounted ? 'animate-fadeIn stagger-4' : 'opacity-0'}`}>
                  <div className="text-3xl font-bold" style={{ color: colorMap.secondary.base }}>5000+</div>
                  <div className="text-sm text-white/90">Produits</div>
                </div>
                <div className={`${mounted ? 'animate-fadeIn stagger-5' : 'opacity-0'}`}>
                  <div className="text-3xl font-bold" style={{ color: colorMap.secondary.base }}>98%</div>
                  <div className="text-sm text-white/90">Satisfaction</div>
                </div>
              </div>
            </div>

            {/* Bottom Decoration */}
            <div className="h-1 w-32 rounded-full" style={{ background: `linear-gradient(90deg, ${colorMap.secondary.base}, transparent)` }}></div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="w-1/2 flex items-center justify-center p-12 relative order-2">
          <div className="w-full max-w-md">
            {/* Skip Button */}
            <div className={`absolute top-12 right-12 ${mounted ? 'animate-fadeIn' : 'opacity-0'}`}>
              <button
                onClick={handleSkip}
                className="group flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition-all duration-300 hover:scale-105"
                style={{ 
                  backgroundColor: colorMap.secondary.opacity10,
                  color: colorMap.secondary.base,
                }}
              >
                <span>Je suis client</span>
                <X size={18} className="transition-transform group-hover:rotate-90" />
              </button>
            </div>

            <FormContent
              isLogin={isLogin}
              step={step}
              formData={formData}
              error={error}
              focusedField={focusedField}
              isLoading={isLoading}
              mounted={mounted}
              isInCite={isInCite}
              setIsInCite={setIsInCite}
              handleInputChange={handleInputChange}
              toggleCategory={toggleCategory}
              setFocusedField={setFocusedField}
              getRoomAddress={getRoomAddress}
              canProceed={canProceed}
              handleNext={handleNext}
              setIsLogin={setIsLogin}
              resetForm={resetForm}
              setStep={setStep}
            />
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden min-h-screen relative z-10">
        {/* Mobile Hero Header */}
        <div className="relative overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-black/50"></div>
            <img 
              src={cite}
              alt="Cité Universitaire" 
              className="w-full h-full object-cover"
            />
          </div>

          {/* Header Content */}
          <div className="relative px-6 pt-6 pb-8 text-white">
            {/* Top Bar */}
            <div className="flex items-center justify-between mb-8">
                       <div className="flex items-center gap-3 rounded-full">
  <img
    src="/logo-image.png"
    alt="Crou Vente"
    className="h-14 w-auto rounded-full"
  />
</div>


              <button
                onClick={handleSkip}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-white/10 backdrop-blur-md border border-white/20 ${mounted ? 'animate-fadeIn' : 'opacity-0'}`}
              >
                <span>Client</span>
                <X size={16} />
              </button>
            </div>

            {/* Hero Text */}
            <div className={`space-y-3 ${mounted ? 'animate-slideUp stagger-2' : 'opacity-0'}`}>
              
              <div className="backdrop-blur-sm bg-black/30 p-4 rounded-xl inline-block">
                <h2 className="text-3xl font-bold leading-tight text-white">
                  Vendez entre vous
                  <br />
                  <span className="text-4xl" style={{ color: colorMap.secondary.base }}>Abobo 1 & 2</span>
                </h2>
              </div>
            </div>
          </div>

          {/* Curved Bottom */}
          <div className="relative h-8 bg-white">
            <div className="absolute inset-0 overflow-hidden">
              <svg 
                className="absolute bottom-0 w-full h-16" 
                viewBox="0 0 1200 120" 
                preserveAspectRatio="none"
              >
                <path 
                  d="M0,0 C300,100 900,100 1200,0 L1200,120 L0,120 Z" 
                  className="fill-current text-white"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Mobile Form */}
        <div className="px-6 pb-12 -mt-4">
          <FormContent
            isLogin={isLogin}
            step={step}
            formData={formData}
            error={error}
            focusedField={focusedField}
            isLoading={isLoading}
            mounted={mounted}
            isInCite={isInCite}
            setIsInCite={setIsInCite}
            handleInputChange={handleInputChange}
            toggleCategory={toggleCategory}
            setFocusedField={setFocusedField}
            getRoomAddress={getRoomAddress}
            canProceed={canProceed}
            handleNext={handleNext}
            setIsLogin={setIsLogin}
            resetForm={resetForm}
            setStep={setStep}
          />
        </div>
      </div>
    </div>
  );
}

// Form Content Component
function FormContent({ 
  isLogin, step, formData, error, focusedField, isLoading, mounted,
  isInCite, setIsInCite,
  handleInputChange, toggleCategory, setFocusedField, getRoomAddress, 
  canProceed, handleNext, setIsLogin, resetForm, setStep 
}) {
  return (
    <div className={`${mounted ? 'animate-slideInRight' : 'opacity-0'}`}>
      {/* Title */}
      <div className="mb-4 mt-5">
        <h2 className="text-4xl font-bold mb-3 display-text" style={{ color: colorMap.primary.base }}>
          {isLogin ? 'Bienvenue !' : step === 1 ? 'Commençons' : `Salut ${formData.name.split(' ')[0]} 👋`}
        </h2>
        <p className="text-lg" style={{ color: colorMap.text.secondary }}>
          {isLogin ? 'Heureux de vous revoir' : 
           step === 1 ? 'Créez votre compte vendeur' :
           step === 2 ? 'Où êtes-vous situé ?' :
           step === 3 ? 'Que vendez-vous ?' :
           step === 4 ? 'Votre boutique' :
           step === 5 ? 'Restons en contact' :
           'Dernière étape'}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div 
          className="mb-6 p-4 rounded-2xl animate-slideUp glass-effect border-l-4"
          style={{ 
            backgroundColor: `${colorMap.status.error}15`,
            borderLeftColor: colorMap.status.error
          }}
        >
          <p className="text-sm font-medium" style={{ color: colorMap.status.error }}>
            {error}
          </p>
        </div>
      )}

      {/* Progress Bar for Registration */}
      {!isLogin && (
        <div className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium" style={{ color: colorMap.text.secondary }}>
              Étape {step} sur 6
            </span>
            <span className="text-sm font-bold" style={{ color: colorMap.secondary.base }}>
              {Math.round((step / 6) * 100)}%
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500 ease-out relative overflow-hidden"
              style={{
                width: `${(step / 6) * 100}%`,
                backgroundColor: colorMap.secondary.base
              }}
            >
              <div className="shimmer-effect absolute inset-0"></div>
            </div>
          </div>
        </div>
      )}

      {/* Forms */}
      <div className="space-y-5" key={isLogin ? 'login' : `signup-${step}`}>
        {/* Login Form */}
        {isLogin && (
          <>
            <InputField
              label="Numéro de téléphone"
              icon={Phone}
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="0102030405"
              focused={focusedField === 'phone'}
              onFocus={() => setFocusedField('phone')}
              onBlur={() => setFocusedField(null)}
              disabled={isLoading}
              pattern="^(07|05|01)\d{8}$"
              title="Le numéro doit commencer par 07, 05 ou 01"
            />

            <InputField
              label="Mot de passe"
              icon={Lock}
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="••••••••"
              focused={focusedField === 'password'}
              onFocus={() => setFocusedField('password')}
              onBlur={() => setFocusedField(null)}
              disabled={isLoading}
            />
          </>
        )}

        {/* Step 1 - Name */}
        {!isLogin && step === 1 && (
          <InputField
            label="Nom complet"
            icon={User}
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Ex: Kouamé Jean"
            focused={focusedField === 'name'}
            onFocus={() => setFocusedField('name')}
            onBlur={() => setFocusedField(null)}
          />
        )}

        {/* Step 2 - Address */}
        {!isLogin && step === 2 && (
          <div className="space-y-5">
            {/* Toggle Switch */}
            <div className="p-5 rounded-2xl glass-effect border-2" style={{ borderColor: colorMap.border.default }}>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-semibold text-base mb-1" style={{ color: colorMap.text.dark }}>
                    Je suis en cité universitaire
                  </p>
                  <p className="text-sm" style={{ color: colorMap.text.muted }}>
                    {isInCite ? 'Abobo 1 ou Abobo 2' : 'Indiquez votre localisation'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsInCite(!isInCite)}
                  className="relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2"
                  style={{
                    backgroundColor: isInCite ? colorMap.primary.base : colorMap.bg.disabled,
                    boxShadow: isInCite ? `0 0 0 4px ${colorMap.primary.opacity10}` : 'none'
                  }}
                >
                  <span
                    className="inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-300"
                    style={{
                      transform: isInCite ? 'translateX(34px)' : 'translateX(4px)'
                    }}
                  />
                </button>
              </div>
            </div>

            {/* Champs d'adresse conditionnels */}
            {isInCite ? (
              <div className="space-y-5 animate-slideUp">
                <div>
                  <label className="block text-sm font-semibold mb-2.5" style={{ color: colorMap.text.dark }}>
                    Cité universitaire
                  </label>
                  <select
                    name="site"
                    value={formData.site}
                    onChange={handleInputChange}
                    className="w-full px-4 py-4 rounded-2xl outline-none transition-all duration-300 border-2 bg-white"
                    style={{
                      borderColor: focusedField === 'site' ? colorMap.primary.base : colorMap.border.default
                    }}
                    onFocus={() => setFocusedField('site')}
                    onBlur={() => setFocusedField(null)}
                  >
                    <option value="">Sélectionnez votre cité</option>
                    {SITES.map(site => (
                      <option key={site} value={site}>{site}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <SelectField
                    label="Bâtiment"
                    name="batiment"
                    value={formData.batiment}
                    onChange={handleInputChange}
                    options={BATIMENTS}
                    focused={focusedField === 'batiment'}
                    onFocus={() => setFocusedField('batiment')}
                    onBlur={() => setFocusedField(null)}
                  />

                  <SelectField
                    label="Palier"
                    name="palier"
                    value={formData.palier}
                    onChange={handleInputChange}
                    options={PALIERS}
                    focused={focusedField === 'palier'}
                    onFocus={() => setFocusedField('palier')}
                    onBlur={() => setFocusedField(null)}
                  />

                  <SelectField
                    label="Chambre"
                    name="chambre"
                    value={formData.chambre}
                    onChange={handleInputChange}
                    options={CHAMBRES}
                    focused={focusedField === 'chambre'}
                    onFocus={() => setFocusedField('chambre')}
                    onBlur={() => setFocusedField(null)}
                  />
                </div>

                {getRoomAddress() && (
                  <div className="p-4 rounded-2xl glass-effect border-l-4 animate-scaleIn" style={{ borderLeftColor: colorMap.primary.base }}>
                    <p className="text-sm font-semibold flex items-center gap-2" style={{ color: colorMap.text.dark }}>
                      <MapPin size={18} style={{ color: colorMap.primary.base }} />
                      {getRoomAddress()}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-5 animate-slideUp">
                <InputField
                  label="Votre localisation"
                  icon={MapPin}
                  type="text"
                  name="site"
                  value={formData.site}
                  onChange={handleInputChange}
                  placeholder="Exemple : Carrefour Akekoi"
                  focused={focusedField === 'site'}
                  onFocus={() => setFocusedField('site')}
                  onBlur={() => setFocusedField(null)}
                  required
                />
                <div className="p-4 rounded-2xl glass-effect border-l-4 animate-scaleIn" style={{ 
                  borderLeftColor: colorMap.secondary.base,
                  backgroundColor: colorMap.secondary.opacity10
                }}>
                  <p className="text-sm font-medium" style={{ color: colorMap.text.dark }}>
                    💫 Indiquez un lieu connu pour faciliter la livraison
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3 - Categories */}
        {!isLogin && step === 3 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="text-sm font-semibold" style={{ color: colorMap.text.dark }}>
                Vos catégories
              </label>
              <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ 
                backgroundColor: colorMap.primary.opacity10,
                color: colorMap.primary.base 
              }}>
                {formData.categories.length} sélectionnée{formData.categories.length > 1 ? 's' : ''}
              </span>
            </div>
            <div className="space-y-2.5 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
              {CATEGORIES.map((category, index) => (
                <button
                  key={category}
                  onClick={() => toggleCategory(category)}
                  className={`category-button w-full p-4 rounded-2xl border-2 flex items-center justify-between ${
                    formData.categories.includes(category) ? 'selected' : ''
                  } animate-slideUp`}
                  style={{
                    borderColor: formData.categories.includes(category) ? colorMap.primary.base : colorMap.border.default,
                    backgroundColor: formData.categories.includes(category) ? colorMap.primary.opacity10 : 'white',
                    animationDelay: `${index * 0.03}s`
                  }}
                >
                  <span className="font-medium text-left" style={{ 
                    color: formData.categories.includes(category) ? colorMap.primary.base : colorMap.text.dark
                  }}>
                    {category}
                  </span>
                  {formData.categories.includes(category) && (
                    <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: colorMap.primary.base }}>
                      <X size={14} color="white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 4 - Shop Details */}
        {!isLogin && step === 4 && (
          <div className="space-y-5">
            <InputField
              label="Nom de votre boutique"
              icon={Store}
              type="text"
              name="shopName"
              value={formData.shopName}
              onChange={handleInputChange}
              placeholder="Ex: La Boutique de Jean"
              focused={focusedField === 'shopName'}
              onFocus={() => setFocusedField('shopName')}
              onBlur={() => setFocusedField(null)}
              required
            />

            <div>
              <label className="block text-sm font-semibold mb-2.5" style={{ color: colorMap.text.dark }}>
                Description <span style={{ color: colorMap.status.error }}>*</span>
              </label>
              <div className="relative">
                <div className="absolute left-4 top-4 z-10" style={{ pointerEvents: 'none' }}>
                  <FileText 
                    size={20}
                    style={{ 
                      color: focusedField === 'shopDescription' ? colorMap.primary.base : colorMap.text.muted,
                      transition: 'color 0.3s'
                    }}
                  />
                </div>
                <textarea
                  name="shopDescription"
                  value={formData.shopDescription}
                  onChange={handleInputChange}
                  placeholder="Parlez-nous de votre boutique..."
                  rows="4"
                  className="w-full pl-12 pr-4 py-4 rounded-2xl outline-none transition-all duration-300 border-2 resize-none bg-white input-focus-ring"
                  style={{
                    borderColor: focusedField === 'shopDescription' ? colorMap.primary.base : colorMap.border.default
                  }}
                  onFocus={() => setFocusedField('shopDescription')}
                  onBlur={() => setFocusedField(null)}
                  required
                />
              </div>
              <p className="text-xs mt-2" style={{ color: colorMap.text.muted }}>
                Minimum 10 caractères
              </p>
            </div>
          </div>
        )}

        {/* Step 5 - Phone */}
        {!isLogin && step === 5 && (
        <>
  <InputField
    label="Numéro de téléphone"
    icon={Phone}
    type="tel"
    name="phone"
    value={formData.phone}
    onChange={handleInputChange}
    placeholder="0102030405"
    focused={focusedField === 'phone'}
    onFocus={() => setFocusedField('phone')}
    onBlur={() => setFocusedField(null)}
    disabled={isLoading}
    pattern="^(07|05|01)\d{8}$"
    inputMode="numeric"
  />
  <p className="text-sm" style={{ color: colorMap.text.muted }}>
    Les acheteurs pourront vous contacter via WhatsApp
  </p>
</>

        )}

        {/* Step 6 - Password */}
        {!isLogin && step === 6 && (
          <>
            <InputField
              label="Mot de passe"
              icon={Lock}
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="••••••••"
              focused={focusedField === 'password'}
              onFocus={() => setFocusedField('password')}
              onBlur={() => setFocusedField(null)}
            />

            <InputField
              label="Confirmer le mot de passe"
              icon={Lock}
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="••••••••"
              focused={focusedField === 'confirmPassword'}
              onFocus={() => setFocusedField('confirmPassword')}
              onBlur={() => setFocusedField(null)}
            />

            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
              <p className="text-sm font-medium animate-slideUp" style={{ color: colorMap.status.error }}>
                ❌ Les mots de passe ne correspondent pas
              </p>
            )}
          </>
        )}

        {/* Action Button */}
        <button
          type='submit'
          onClick={handleNext}
          disabled={!canProceed() || isLoading}
          className="group w-full py-4 lg:py-5 rounded-2xl font-bold text-base lg:text-lg flex items-center justify-center gap-3 transition-all duration-300 mt-8 relative overflow-hidden"
          style={{
            backgroundColor: canProceed() && !isLoading ? colorMap.primary.base : colorMap.bg.disabled,
            color: colorMap.bg.white,
            cursor: canProceed() && !isLoading ? 'pointer' : 'not-allowed',
            transform: 'scale(1)',
            boxShadow: canProceed() && !isLoading ? '0 20px 40px -12px rgba(34, 197, 94, 0.4)' : 'none'
          }}
          onMouseEnter={(e) => {
            if (canProceed() && !isLoading) {
              e.currentTarget.style.transform = 'scale(1.02) translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 25px 50px -12px rgba(34, 197, 94, 0.5)';
            }
          }}
          onMouseLeave={(e) => {
            if (canProceed() && !isLoading) {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 20px 40px -12px rgba(34, 197, 94, 0.4)';
            }
          }}
        >
          {canProceed() && !isLoading && (
            <div className="shimmer-effect absolute inset-0"></div>
          )}
          {isLoading ? (
            <>
              <Loader2 size={22} className="animate-spin" />
              <span>Chargement...</span>
            </>
          ) : (
            <>
              <span className="relative z-10">
                {isLogin ? 'Se connecter' : (step === 6 ? '🎉 Créer mon compte' : 'Continuer')}
              </span>
              <ArrowRight size={22} className="transition-transform duration-300 group-hover:translate-x-2 relative z-10" />
            </>
          )}
        </button>

        {/* Toggle Authentication Mode */}
        <div className="text-center mt-6 lg:mt-8">
          {isLogin ? (
            <p className="text-xs lg:text-sm" style={{ color: colorMap.text.secondary }}>
              Nouveau sur Crou Vente ?{' '}
              <button
                onClick={() => { setIsLogin(false); resetForm(); }}
                disabled={isLoading}
                className="font-bold transition-all duration-200"
                style={{ 
                  color: colorMap.primary.base,
                  opacity: isLoading ? 0.6 : 1 
                }}
              >
                Créer un compte
              </button>
            </p>
          ) : (
            <p className="text-xs lg:text-sm" style={{ color: colorMap.text.secondary }}>
              Déjà inscrit ?{' '}
              <button
                onClick={() => { setIsLogin(true); resetForm(); }}
                disabled={isLoading}
                className="font-bold transition-all duration-200"
                style={{ 
                  color: colorMap.primary.base,
                  opacity: isLoading ? 0.6 : 1 
                }}
              >
                Se connecter
              </button>
            </p>
          )}
        </div>

        {/* Back Button */}
        {!isLogin && step > 1 && !isLoading && (
          <button
            onClick={() => setStep(step - 1)}
            className="w-full py-2.5 lg:py-3 font-semibold transition-all duration-200 rounded-xl text-sm lg:text-base"
            style={{ 
              color: colorMap.text.secondary,
              backgroundColor: 'transparent'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = colorMap.bg.light;
              e.target.style.color = colorMap.text.dark;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = colorMap.text.secondary;
            }}
          >
            ← Retour
          </button>
        )}
      </div>
    </div>
  );
}

// Input Field Component
function InputField({ label, icon: Icon, type, name, value, onChange, placeholder, focused, onFocus, onBlur, disabled, pattern, title, required }) {
  return (
    <div>
      <label className="block text-sm font-semibold mb-2.5" style={{ color: colorMap.text.dark }}>
        {label} {required && <span style={{ color: colorMap.status.error }}>*</span>}
      </label>
      <div className="relative">
        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10" style={{ pointerEvents: 'none' }}>
          <Icon 
            size={20}
            style={{ 
              color: focused ? colorMap.primary.base : colorMap.text.muted,
              transition: 'color 0.3s'
            }}
          />
        </div>
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          pattern={pattern}
          title={title}
          className="w-full pl-12 pr-4 py-4 rounded-2xl outline-none transition-all duration-300 border-2 bg-white input-focus-ring"
          style={{
            borderColor: focused ? colorMap.primary.base : colorMap.border.default,
            opacity: disabled ? 0.6 : 1
          }}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      </div>
    </div>
  );
}

// Select Field Component
function SelectField({ label, name, value, onChange, options, focused, onFocus, onBlur }) {
  return (
    <div>
      <label className="block text-xs font-semibold mb-2" style={{ color: colorMap.text.dark }}>
        {label}
      </label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        className="w-full px-3 py-4 rounded-2xl outline-none transition-all duration-300 border-2 bg-white text-sm"
        style={{
          borderColor: focused ? colorMap.primary.base : colorMap.border.default
        }}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        <option value="">-</option>
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
}